package top.wcpe.allincore.ui.runtime.internal.concurrent

/**
 * 并发原语契约的 JS actual：JS 单线程事件循环无真并发，全部退化为平凡实现。
 */

/** JS：单线程无竞争，锁为空操作。 */
internal actual class SyncLock actual constructor() {
    actual fun acquire() {
        // JS 单线程：无需互斥。
    }

    actual fun release() {
        // JS 单线程：无需互斥。
    }
}

/** JS：单线程下即一个惰性初始化的普通容器。 */
internal actual class ThreadLocalRef<T> actual constructor(private val initial: () -> T) {
    private var initialized = false
    private var value: T? = null

    actual fun get(): T {
        if (!initialized) {
            value = initial()
            initialized = true
        }
        @Suppress("UNCHECKED_CAST")
        return value as T
    }

    actual fun set(value: T) {
        this.value = value
        initialized = true
    }
}

/**
 * JS：ArrayList + `===` 的身份集（订阅者/依赖集规模小，线性代价可接受）。
 * 不依赖元素的 equals/hashCode——订阅者可能覆写 equals，身份语义必须用引用比较。
 */
internal actual fun <T> newIdentitySet(): MutableSet<T> = IdentityArraySet()

/**
 * JS：普通 HashMap（键为无 equals/hashCode 覆写的对象时天然身份语义，如 UIElement）。
 * 单线程无并发，调用方须保证键类型不覆写 equals（与 expect KDoc 契约一致）。
 */
internal actual fun <K : Any, V> newIdentityMap(): MutableMap<K, V> = HashMap()

/** JS 单线程：普通 HashMap（UIElement 无 equals 覆写，身份即相等）。 */
internal actual fun <K : Any, V> newConcurrentIdentityMap(): MutableMap<K, V> = HashMap()

/** JS 单线程：普通 LinkedHashSet（保插入序，与 JVM keySet 遍历语义近似）。 */
internal actual fun <T> newConcurrentSet(): MutableSet<T> = LinkedHashSet()

/** JS：事件循环无同步阻塞能力；状态机延迟须走异步 executor 路径，落到这里即为用法错误。 */
internal actual fun blockingDelayForTest(millis: Long) {
    TODO("JS 端无同步阻塞延迟（状态机 delay 须经 PlatformScheduler 异步恢复）")
}

/** JS：单线程下的普通布尔 var（无真并发，compareAndSet 平凡）。 */
internal actual class AtomicBooleanFlag actual constructor(initial: Boolean) {
    private var value: Boolean = initial

    actual fun get(): Boolean = value

    actual fun set(value: Boolean) {
        this.value = value
    }

    actual fun compareAndSet(expected: Boolean, update: Boolean): Boolean {
        if (value != expected) {
            return false
        }
        value = update
        return true
    }
}

/** [newIdentitySet] 的 JS 实现：AbstractMutableSet 只读契约 + ArrayList 背书。 */
private class IdentityArraySet<T> : AbstractMutableSet<T>() {
    private val items = ArrayList<T>()

    override val size: Int
        get() = items.size

    override fun add(element: T): Boolean {
        if (items.any { it === element }) return false
        items.add(element)
        return true
    }

    override fun remove(element: T): Boolean {
        val idx = items.indexOfFirst { it === element }
        if (idx < 0) return false
        items.removeAt(idx)
        return true
    }

    override fun contains(element: T): Boolean = items.any { it === element }

    override fun iterator(): MutableIterator<T> = items.iterator()
}

/** JS：单线程下普通 HashMap 即天然原子。 */
internal actual class ConcurrentMutableMap<K : Any, V : Any> actual constructor() {
    private val delegate = HashMap<K, V>()

    actual fun putIfAbsent(key: K, value: V): V? {
        val existing = delegate[key]
        if (existing != null) return existing
        delegate[key] = value
        return null
    }

    actual fun computeIfAbsent(key: K, compute: (K) -> V): V {
        val existing = delegate[key]
        if (existing != null) return existing
        val created = compute(key)
        delegate[key] = created
        return created
    }

    actual operator fun get(key: K): V? = delegate[key]

    actual operator fun set(key: K, value: V) {
        delegate[key] = value
    }

    actual fun remove(key: K): V? = delegate.remove(key)

    actual fun remove(key: K, expected: V): Boolean {
        if (delegate[key] != expected) return false
        delegate.remove(key)
        return true
    }

    actual fun clear() = delegate.clear()

    actual fun size(): Int = delegate.size

    actual fun entriesSnapshot(): List<Pair<K, V>> = delegate.entries.map { it.key to it.value }
}

/** JS：普通 var 引用。 */
internal actual class AtomicRef<T> actual constructor(initial: T) {
    private var value: T = initial

    actual fun get(): T = value

    actual fun set(value: T) {
        this.value = value
    }
}

/** JS：普通 ArrayList（单线程；回调遍历处由调用方按需快照）。 */
internal actual fun <T> newConcurrentList(): MutableList<T> = ArrayList()

/** JS：普通 var 计数器。 */
internal actual class AtomicIntCounter actual constructor(initial: Int) {
    private var value: Int = initial

    actual fun get(): Int = value

    actual fun set(value: Int) {
        this.value = value
    }

    actual fun incrementAndGet(): Int = ++value

    actual fun decrementAndGet(): Int = --value
}

/** JS：普通 var 计数器。 */
internal actual class AtomicLongCounter actual constructor(initial: Long) {
    private var value: Long = initial

    actual fun get(): Long = value

    actual fun set(value: Long) {
        this.value = value
    }

    actual fun compareAndSet(expected: Long, update: Long): Boolean {
        if (value != expected) return false
        value = update
        return true
    }

    actual fun incrementAndGet(): Long = ++value
}

/** [newWeakKeyedMap] 的 JS 实现：普通 HashMap（弱语义由调用方清理路径兜底，见 expect KDoc）。 */
internal actual fun <K : Any, V : Any> newWeakKeyedMap(): MutableMap<K, V> = HashMap()
